import React, { useCallback, useEffect, useState, memo } from 'react'
import { ZinCheckbox, ZinInput, ZinRadio, ZinSelect, ZinSelectOption, ZinTextarea } from '@tnl/zin-react'
import { IFormField } from '@hooks/useForm'
import { InternationalPhone } from '@ui/InternationalPhone/InternationalPhone'
import { ParsedCountry } from 'react-international-phone'
import clsx from 'clsx'
import { convert3DigitCountryCodeTo2Digit } from '@utils/convert3DigitCountryCodeTo2Digit'

interface FormFieldProps {
  field: IFormField
  form: any
  /**
   * When true, mount ZinInput after one frame (requestAnimationFrame).
   * Use in modal/auth flows so ZinInput mounts after URL/location updates have settled,
   * avoiding Stencil's getHostRef error from componentDidLoad validation debounce.
   */
  deferZinInputMount?: boolean
}

export const FormField: React.FC<FormFieldProps> = memo(({ field, form, deferZinInputMount }) => {
  if (!field || !form) return null

  const { disabled, id, label, sanitizeInput, name, required, type } = field
  const { values, errors, handleChange, validateField } = form

  const status = errors?.[id] ? 'error' : 'none'
  const statusText = errors?.[id] ?? ''
  const value = values?.[id]

  const [zinInputReady, setZinInputReady] = useState(!deferZinInputMount)

  useEffect(() => {
    if (!deferZinInputMount || zinInputReady) return
    const id = requestAnimationFrame(() => setZinInputReady(true))
    return () => cancelAnimationFrame(id)
  }, [deferZinInputMount, zinInputReady])

  const handleEvent = useCallback(
    (e: any) => {
      const targetVal = e.detail
      const checked = e?.target?.checked
      const localName = e?.target?.localName

      const finalVal = typeof checked !== 'undefined' && localName === 'zin-checkbox' ? checked : targetVal

      // sanitizeInput value for number inputs
      if (sanitizeInput) {
        const sanitizedValue = sanitizeInput(finalVal)
        handleChange(name, sanitizedValue)
        return
      }

      handleChange(name, finalVal)
    },
    [name, handleChange],
  )

  const stripPhonePrefixAndDialCode = (phoneNumber: string, dialCode?: string): string => {
    if (!phoneNumber) return ''

    // If it doesn't start with +, return as is
    if (!phoneNumber.startsWith('+')) {
      return phoneNumber
    }

    let strippedPhoneNumber = phoneNumber.slice(1)

    // Remove dial code from the start of the phone number
    if (dialCode && strippedPhoneNumber.startsWith(dialCode)) {
      strippedPhoneNumber = strippedPhoneNumber.slice(dialCode.length)
    }

    return strippedPhoneNumber
  }

  // Custom handler for InternationalPhone component to strip non-numeric characters (except leading +) and shape data
  const handleTelChange = useCallback(
    (fieldValue: string, meta?: { country: ParsedCountry; inputValue: string }) => {
      const customMeta = {
        ...meta,
        inputValue: stripPhonePrefixAndDialCode(fieldValue, meta?.country?.dialCode),
      }
      handleChange(name, customMeta)
    },
    [name, handleChange],
  )

  const commonProps = {
    id,
    label: label ?? '',
    name,
    required: !!required,
    disabled: !!disabled,
    onBlur: () => validateField(id),
    novalidate: true,
    status,
    statusText,
  }

  switch (type) {
    case 'text':
    case 'email':
    case 'password':
    case 'number': {
      if (deferZinInputMount && !zinInputReady) return null
      return (
        <ZinInput
          {...commonProps}
          max={field.max}
          min={field.min}
          maxLength={field.maxLength}
          minLength={field.minLength}
          onInputChange={handleEvent}
          type={type}
          value={value ?? ''}
        />
      )
    }

    case 'tel': {
      // Get default country from phoneCountry field in form values or field.defaultCountry
      // todo: this logic does not belong here and needs to be moved to InternationalPhone or somewhere else
      const phoneCountry = value?.countryCode ? value.countryCode : 'us'
      const defaultCountry = convert3DigitCountryCodeTo2Digit(phoneCountry) || undefined

      return (
        <InternationalPhone
          className={clsx('input__container', { 'input--error': status === 'error' })}
          {...commonProps}
          defaultCountry={defaultCountry}
          disableDialCodeAndPrefix
          showDisabledDialCodeAndPrefix
          error={statusText || false}
          onChange={handleTelChange}
          value={value?.phoneNumber ?? ''}
        />
      )
    }

    case 'checkbox':
      return (
        <ZinCheckbox
          {...commonProps}
          checked={!!value}
          onInputChange={handleEvent}
          value={value ?? ''}
        />
      )

    case 'radio':
      return (
        <ZinRadio
          {...commonProps}
          className="items-start"
          checked={values?.[name] === field.value}
          onInputChange={handleEvent}
          value={field.value}
        />
      )

    case 'select':
      return (
        <ZinSelect
          {...commonProps}
          onValueChange={handleEvent}
          value={value}>
          {field?.options?.map((option, index) => (
            <ZinSelectOption
              // eslint-disable-next-line react/no-array-index-key
              key={`${name}-${option.value}-${index}`}
              value={option.value}
              disabled={option.disabled || false}>
              {option.label}
            </ZinSelectOption>
          ))}
        </ZinSelect>
      )

    case 'textarea':
      return (
        <ZinTextarea
          {...commonProps}
          onInputChange={handleEvent}
          value={value ?? ''}
        />
      )

    default:
      return null
  }
})

export default FormField
